// @ts-nocheck
/**
 * CyberSweep Team Dashboard
 *
 * Shows team-wide view of CS Audits for supervisors/managers.
 * Displays workload distribution, status breakdown, and overdue items.
 */

import React, { useMemo, useState } from 'react';
import { Users, Clock, AlertTriangle, CheckCircle2, FileText, BarChart3, User, ChevronRight, ChevronDown } from 'lucide-react';
import {
  useCSAudits,
  useAccountIdToName,
  resolveCSAuditListClientName,
  useAuditorIdToName,
} from '../../services/queries/cyberSweepQueries';
import { useUsers } from '../../services/queries/userQueries';
import { StandardSearchBar } from '../../components/standard';
import { formatCyberSweepDisplayDate, parseCyberSweepDateToMs } from '../../utils/cyberSweepDisplayDate';
import { CheckForNewClientsButton } from '../../components/credit-block/CheckForNewClientsButton';
import { getCustomSuffix } from '../../config/gobodoConfig';

interface CyberSweepTeamDashboardProps {
  onViewAudit?: (auditId: string) => void;
  onBack?: () => void;
}

// Field resolver for multi-name fields
const g = (r: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (r[k] !== undefined && r[k] !== null) return r[k];
  }
  return undefined;
};

// Check if a value looks like a numeric ID (not a name)
const isNumericId = (value: unknown): boolean => {
  if (value == null) return false;
  return /^\d+$/.test(String(value).trim());
};

// Values that should be treated as "Unassigned"
const UNASSIGNED_VALUES = ['--none--', 'none', '', 'null', 'undefined'];

const isUnassignedValue = (value: unknown): boolean => {
  if (!value) return true;
  const strVal = String(value).trim().toLowerCase();
  return UNASSIGNED_VALUES.includes(strVal);
};

// Get auditor display name, preferring name fields over ID fields
const getAuditorDisplayName = (
  audit: Record<string, unknown>,
  data: Record<string, unknown>,
  auditorIdToName: Record<string, string> | undefined,
  userIdToNameMap: Record<string, string>
): string => {
  // 1. Try name fields first (from newly created audits)
  const nameFromField =
    audit.Auditor_Name__c ?? audit.auditorName ?? audit.AuditorName__c ??
    data.Auditor_Name__c ?? data.auditorName ?? data.AuditorName__c;

  if (nameFromField && !isNumericId(nameFromField) && !isUnassignedValue(nameFromField)) {
    return String(nameFromField).trim();
  }

  // 2. Get the auditor ID
  const auditorId = String(
    audit.Auditor__c ?? audit.auditor ?? audit.custom__auditor ??
    data.Auditor__c ?? data.auditor ?? data.custom__auditor ?? ''
  ).trim();

  if (!auditorId || isUnassignedValue(auditorId)) {
    return 'Unassigned';
  }

  // 3. Try to look up the ID in auditorIdToName map (from Gobodo graph)
  const nameFromGraph = auditorIdToName?.[auditorId];
  if (nameFromGraph && !isUnassignedValue(nameFromGraph)) {
    return nameFromGraph;
  }

  // 4. Try userIdToNameMap (from Users query)
  const nameFromUsers = userIdToNameMap[auditorId];
  if (nameFromUsers && !isUnassignedValue(nameFromUsers)) {
    return nameFromUsers;
  }

  // 5. If it's a numeric ID, show as "User {ID}" to indicate it couldn't be resolved
  if (isNumericId(auditorId)) {
    return `User ${auditorId}`;
  }

  // 6. If auditorId itself looks like an unassigned value, return "Unassigned"
  if (isUnassignedValue(auditorId)) {
    return 'Unassigned';
  }

  // 7. Otherwise assume it's already a name
  return auditorId;
};

const isOverdue = (rawDue: unknown): boolean => {
  const ms = parseCyberSweepDateToMs(rawDue);
  if (ms == null) return false;
  const dueDate = new Date(ms);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dueDate < today;
};

const getPhaseStatusColor = (status: string) => {
  if (status?.includes('Completed') || status?.includes('Complete'))
    return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200';
  if (status?.includes('In Progress'))
    return 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200';
  if (status?.includes('Road Block'))
    return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200';
  return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
};

interface TeamMemberStats {
  id: string;
  name: string;
  total: number;
  inProgress: number;
  completed: number;
  overdue: number;
  audits: Array<{
    id: string;
    name: string;
    clientName: string;
    phaseStatus: string;
    dueDate: string;
    dueDateRaw: string;
    isOverdue: boolean;
  }>;
}

const CyberSweepTeamDashboard: React.FC<CyberSweepTeamDashboardProps> = ({ onViewAudit, onBack }) => {
  const { data: csAuditRecords = [], isLoading } = useCSAudits(undefined, { fetchAllCSAudits: true });
  const { data: accountIdToName } = useAccountIdToName(csAuditRecords as Record<string, unknown>[]);
  const { data: auditorIdToName } = useAuditorIdToName(csAuditRecords as Record<string, unknown>[]);
  const { data: users = [] } = useUsers({});

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMembers, setExpandedMembers] = useState<Set<string>>(new Set());
  const [showAllAudits, setShowAllAudits] = useState<Set<string>>(new Set());

  // Build user ID to name map - map ALL possible ID fields to user's display name
  const userIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    users.forEach(user => {
      const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || (user as any).name || '';
      if (!name) return;

      // Map user.id
      if (user.id) {
        map[user.id] = name;
      }

      // Map user.key (Gobodo graph key)
      const userKey = (user as Record<string, unknown>).key;
      if (userKey && userKey !== user.id) {
        map[String(userKey)] = name;
      }

      // Map keycloakUserId (numeric IDs like "37876", "1543")
      if (user.keycloakUserId) {
        map[user.keycloakUserId] = name;
      }

      // Map graphObjectKey (another possible Gobodo key)
      if (user.graphObjectKey && user.graphObjectKey !== user.id) {
        map[user.graphObjectKey] = name;
      }
    });
    return map;
  }, [users]);

  // Map all audits and group by auditor
  const teamStats = useMemo(() => {
    const auditorMap: Record<string, TeamMemberStats> = {};
    const customSuffix = getCustomSuffix();

    (csAuditRecords as Record<string, unknown>[]).forEach(audit => {
      const data = (audit.data as Record<string, unknown>) ?? audit;
      // Use new helper that prioritizes name fields over ID fields
      const auditorName = getAuditorDisplayName(audit, data, auditorIdToName, userIdToNameMap);
      const auditorRaw = String(
        audit.Auditor__c ?? audit.auditor ?? data.Auditor__c ?? data.auditor ?? ''
      ).trim();
      const clientName = resolveCSAuditListClientName(audit, customSuffix, accountIdToName);
      const phaseStatus = String(g(audit, data, 'phaseStatus', 'Phase_Status__c', 'PhaseStatus__c', 'custom__phase_status') ?? '');
      const rawDueDate = String(g(audit, data, 'dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date') ?? '');
      const name = String(g(audit, data, 'name', 'Name__c', 'Name') ?? audit.id ?? '');
      const auditId = String(audit.id ?? audit.key ?? '');

      const auditEntry = {
        id: auditId,
        name,
        clientName,
        phaseStatus,
        dueDate: formatCyberSweepDisplayDate(rawDueDate),
        dueDateRaw: rawDueDate,
        isOverdue: isOverdue(rawDueDate),
      };

      if (!auditorMap[auditorName]) {
        auditorMap[auditorName] = {
          id: auditorRaw || auditorName,
          name: auditorName,
          total: 0,
          inProgress: 0,
          completed: 0,
          overdue: 0,
          audits: [],
        };
      }

      auditorMap[auditorName].total++;
      auditorMap[auditorName].audits.push(auditEntry);

      if (phaseStatus.includes('In Progress')) {
        auditorMap[auditorName].inProgress++;
      }
      if (phaseStatus.includes('Completed') || phaseStatus.includes('Complete')) {
        auditorMap[auditorName].completed++;
      }
      if (auditEntry.isOverdue) {
        auditorMap[auditorName].overdue++;
      }
    });

    // Sort each member's audits by due date
    Object.values(auditorMap).forEach(member => {
      member.audits.sort((a, b) => (a.dueDateRaw || '9999').localeCompare(b.dueDateRaw || '9999'));
    });

    // Convert to array and sort by total audits descending
    return Object.values(auditorMap)
      .filter(member => {
        if (searchQuery) {
          return member.name.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return true;
      })
      .sort((a, b) => b.total - a.total);
  }, [csAuditRecords, accountIdToName, auditorIdToName, userIdToNameMap, searchQuery]);

  // Overall stats
  const overallStats = useMemo(() => {
    const all = teamStats.reduce(
      (acc, member) => ({
        total: acc.total + member.total,
        inProgress: acc.inProgress + member.inProgress,
        completed: acc.completed + member.completed,
        overdue: acc.overdue + member.overdue,
      }),
      { total: 0, inProgress: 0, completed: 0, overdue: 0 }
    );
    return { ...all, teamMembers: teamStats.length };
  }, [teamStats]);

  const toggleMember = (memberId: string) => {
    setExpandedMembers(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) {
        next.delete(memberId);
      } else {
        next.add(memberId);
      }
      return next;
    });
  };

  // Status distribution for chart-like display
  const statusDistribution = useMemo(() => {
    const distribution: Record<string, number> = {};
    (csAuditRecords as Record<string, unknown>[]).forEach(audit => {
      const data = (audit.data as Record<string, unknown>) ?? audit;
      const phaseStatus = String(g(audit, data, 'phaseStatus', 'Phase_Status__c', 'PhaseStatus__c', 'custom__phase_status') ?? 'Not Started');
      distribution[phaseStatus] = (distribution[phaseStatus] || 0) + 1;
    });
    return Object.entries(distribution)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [csAuditRecords]);

  return (
    <div className="space-y-3">
      {/* Header - Compact */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Team Dashboard</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">({overallStats.teamMembers} members)</span>
        </div>
        <CheckForNewClientsButton />
      </div>

      {/* Overall Stats Cards - Compact */}
      <div className="grid grid-cols-5 gap-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Members</span>
            <Users className="h-3.5 w-3.5 text-purple-500" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{overallStats.teamMembers}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Audits</span>
            <FileText className="h-3.5 w-3.5 text-gray-400" />
          </div>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{overallStats.total}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Overdue</span>
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
          </div>
          <p className="text-lg font-bold text-red-600 dark:text-red-400">{overallStats.overdue}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">In Progress</span>
            <Clock className="h-3.5 w-3.5 text-blue-500" />
          </div>
          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{overallStats.inProgress}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">Completed</span>
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
          </div>
          <p className="text-lg font-bold text-green-600 dark:text-green-400">{overallStats.completed}</p>
        </div>
      </div>

      {/* Status Distribution - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-gray-400" />
          Status Distribution
        </h3>
        <div className="space-y-2">
          {statusDistribution.map(([status, count]) => {
            const percentage = overallStats.total > 0 ? (count / overallStats.total) * 100 : 0;
            return (
              <div key={status}>
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="text-gray-700 dark:text-gray-300">{status || 'Not Started'}</span>
                  <span className="text-gray-500 dark:text-gray-400">{count} ({percentage.toFixed(0)}%)</span>
                </div>
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      status.includes('Completed') || status.includes('Complete')
                        ? 'bg-green-500'
                        : status.includes('In Progress')
                        ? 'bg-blue-500'
                        : status.includes('Road Block')
                        ? 'bg-red-500'
                        : 'bg-gray-400'
                    }`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Team Member Search - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm px-3 py-2">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search team members..."
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Team Members Workload - Compact */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Workload by Team Member ({teamStats.length})
          </h3>
        </div>

        {isLoading ? (
          <div className="px-4 py-8 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mb-2"></div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        ) : teamStats.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Users className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchQuery ? 'No team members match your search' : 'No audit data available'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[calc(100vh-420px)] overflow-y-auto">
            {teamStats.map(member => {
              const isExpanded = expandedMembers.has(member.id);
              return (
                <div key={member.id}>
                  {/* Member Header */}
                  <div
                    onClick={() => toggleMember(member.id)}
                    className="px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button className="p-0.5">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                            <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{member.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {member.total} audit{member.total !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {member.overdue > 0 && (
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            <span className="text-xs font-medium">{member.overdue}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-blue-600 dark:text-blue-400">{member.inProgress} active</span>
                          <span className="text-green-600 dark:text-green-400">{member.completed} done</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Member's Audits (Expanded) */}
                  {isExpanded && (
                    <div className="bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                      {member.audits.length === 0 ? (
                        <div className="px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                          No audits assigned
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                          {(showAllAudits.has(member.id) ? member.audits : member.audits.slice(0, 10)).map(audit => (
                            <div
                              key={audit.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewAudit?.(audit.id);
                              }}
                              className="px-4 py-2 pl-12 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                                      {audit.name}
                                    </span>
                                    {audit.isOverdue && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                        Overdue
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    {audit.clientName}
                                    {audit.dueDate && ` • Due: ${audit.dueDate}`}
                                  </p>
                                </div>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPhaseStatusColor(audit.phaseStatus)}`}>
                                  {audit.phaseStatus || 'Not Started'}
                                </span>
                              </div>
                            </div>
                          ))}
                          {member.audits.length > 10 && (
                            <div className="px-4 py-1.5 text-center">
                              {showAllAudits.has(member.id) ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAllAudits(prev => {
                                      const next = new Set(prev);
                                      next.delete(member.id);
                                      return next;
                                    });
                                  }}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                                >
                                  Show less
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAllAudits(prev => new Set(prev).add(member.id));
                                  }}
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                                >
                                  +{member.audits.length - 10} more audits
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CyberSweepTeamDashboard;
